/**
 * useSessionManagement — Session lifecycle: resets, history, timeouts, refresh
 * Extracted VERBATIM from SimpleCopilotPanel.tsx
 * 
 * Contains:
 *   - maintainHistory (L102-122) — sessionStorage history CRUD
 *   - sendResetToBackend (L885-928) — API reset call
 *   - clearConversation (L604-684) — full reset with API + separator
 *   - handleResetConversation (L693-739) — UI-only reset
 *   - handleFullClear (L742-771) — complete clear + prompt gallery refresh
 *   - 4 effects: appName event (L224), history persistence (L244), session timeout (L294), refresh (L359)
 * 
 * Pre-existing bugs (copied verbatim — DO NOT FIX):
 *   - BUG #2 (R8): Session timeout stale state — messages.some(...) check
 *   - R22: Polling stale closure on handleFullClear — separate effect, not in this hook
 *   - R24: sendResetToBackend forward reference — both in this hook = same scope, ordering preserved
 * 
 * Risks handled:
 *   - R1: Cross-hook state — all state received from orchestrator, not owned
 *   - R2: 3 reset functions cross ALL boundaries — all co-located in this hook
 *   - R3: globalUploadedFiles — not directly written here (handled by orchestrator + useFormHandler)
 *   - R14: currentActiveFormId — setter received from orchestrator
 * 
 * SAFE REFACTORING: No logic changes. Code copied exactly as-is.
 */

import * as React from 'react';
import { postExternalConsumptionAPI } from '../../../utils/httpUtilsExternalAPI';
import { COPILOT_CONSTANTS } from '../constants';
import type { Message } from '../interfaces';
import type { CopilotChatParams } from './CopilotChatParams';
import { onSessionExpired, clearSessionExpiredCallback } from '../../../services/sessionExpirationService';
import { PromptGalleryService } from '../PromptGallery/services/promptGalleryService';

/**
 * Hook for session lifecycle management
 * Returns clearConversation, handleResetConversation, handleFullClear, maintainHistory
 */
export function useSessionManagement(params: CopilotChatParams) {
  const {
    messages,
    setMessages,
    setLoading,
    followUpQuestions,
    setFollowUpQuestions,
    setCurrentActiveFormId,
    currentActiveFormId,
    submittedFormIds,
    setSubmittedFormIds,
    setPersistentFormValues,
    setCurrentTicketNumber,
    setShowChangeTopic,
    setIsPromptGalleryOpen,
    setAppNameVersion,
    sessionIdRef,
    controlRef,
    scrollRef,
    effectiveApi,
    mcapsTelemetry,
    platformAuthClient,
    platformContext,
  } = params;

  // Verbatim from SimpleCopilotPanel.tsx L102-122
  const maintainHistory = React.useCallback((type: 'SET' | 'GET' | 'RESET', historyObject?: any) => {
    try {
      if (type === 'SET') {
        if (historyObject) {
          sessionStorage.setItem('copilotHistory', JSON.stringify(historyObject));
        } else {
          sessionStorage.removeItem('copilotHistory');
        }
        return null;
      }
      if (type === 'GET') {
        const raw = sessionStorage.getItem('copilotHistory');
        return raw ? JSON.parse(raw) : null;
      }
      if (type === 'RESET') {
        sessionStorage.removeItem('copilotHistory');
        return null;
      }
    } catch { }
    return null;
  }, []);

  // Verbatim from SimpleCopilotPanel.tsx L885-928
  // R24: Forward reference — clearConversation at L615 calls this. Both in same hook scope = ordering preserved.
  const sendResetToBackend = async (showResetMessage: boolean = false) => {
    try {
      if (!effectiveApi) {
        return;
      }
      
      // Add reset message to chat if requested
      if (showResetMessage) {
        const resetMessage: Message = {
          id: COPILOT_CONSTANTS.MESSAGE_ID_PREFIXES.USER + Date.now(),
          role: 'user',
          content: 'reset',
          timestamp: Date.now()
        };
        setMessages(prev => [...prev, resetMessage]);
      }
      
      // Include appName context for ECIF integration
      const appName = sessionStorage.getItem('mcapsCopilot_appName');
      const apiPayload = {
        query: 'reset',
        sessionId: sessionIdRef.current,
        isReset: true, // Optional flag to indicate this is a reset request
        ...(appName && { appName: appName })
      };      
      
      const response = await postExternalConsumptionAPI(
        effectiveApi, 
        apiPayload, 
        platformAuthClient
      );
      // Log telemetry for reset action
      mcapsTelemetry.logQuerySubmitted({
        query: 'reset',
        component: 'SimpleCopilotPanel',
        action: 'submit'
      });
      
      return response;
    } catch (error: any) {
      console.error('❌ Reset API call failed:', error);
      mcapsTelemetry.logError({
        query: 'Reset API call failed',
        error: error?.message || String(error)
      });
      // Don't throw - reset should still work on frontend even if backend fails
    }
  };

  // Verbatim from SimpleCopilotPanel.tsx L604-684
  const clearConversation = async () => {
    mcapsTelemetry.logNewConversationAction({
      action: 'click',
      component: 'SimpleCopilotPanel'
    });
    
    setLoading(true);
    setFollowUpQuestions([]); // Clear follow-up questions
    
    try {
      // Send reset to backend (no UI message shown)
      const response = await sendResetToBackend(false);
      
      // After receiving backend response, add new conversation separator directly
      if (response && response.data) {
        setMessages(prev => {
          const lastMessage = prev[prev.length - 1];
          const isLastMessageNewConversationSeparator = lastMessage && 
            lastMessage.role === 'separator' && 
            lastMessage.showNewConversation === true;
          
          if (isLastMessageNewConversationSeparator) {
            return prev;
          }
          
          const separatorMessage: Message = {
            id: COPILOT_CONSTANTS.MESSAGE_ID_PREFIXES.SYSTEM + Date.now(),
            role: 'separator',
            content: COPILOT_CONSTANTS.MESSAGES.NEW_CONVERSATION,
            timestamp: Date.now(),
            showNewConversation: true,
            separator: 'NewConversation',
            from: 'newConversation'
          };
          
          // Scroll to bottom to show separator
          setTimeout(() => {
            if (scrollRef.current) {
              scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
            }
          }, 150);
          
          return [...prev, separatorMessage];
        });
      }
    } catch (error) {
      console.error('Error during reset:', error);
    } finally {
      setLoading(false);
    }
    
    // Generate new session ID for fresh conversation
    sessionIdRef.current = COPILOT_CONSTANTS.SESSION.ID_PREFIX + Math.random().toString(36).slice(2);
    
    if (controlRef.current?.setInputText) {
      controlRef.current.setInputText("");
    }
    setIsPromptGalleryOpen(false);
    setShowChangeTopic(true);
    
    // Don't add separator here - wait for yes/no response
    
    // Clear other state
    setPersistentFormValues({});
    setCurrentActiveFormId(null);
    setSubmittedFormIds(new Set());
    
    // Clear any stored ticket number
    sessionStorage.removeItem('mcapsCopilot_ticketNumber');
    setCurrentTicketNumber(null);
    
    setTimeout(() => {
      const chatInput = document.querySelector('.textarea-chat textarea') || 
                      document.querySelector('.textarea-chat [role="textbox"]') ||
                      document.querySelector('.fai-EditorInput textarea') ||
                      document.querySelector('.fai-EditorInput [role="textbox"]');
      if (chatInput && chatInput instanceof HTMLElement) {
        chatInput.focus();
      }
    }, 200);
  };

  // Verbatim from SimpleCopilotPanel.tsx L693-739
  const handleResetConversation = () => {
    // Remove any session timeout messages and empty system messages
    const filteredMessages = messages.filter(msg => {
      // Remove session timeout messages
      if (msg.role === 'separator' && msg.showSignInButton === true) {
        return false;
      }
      // Remove empty system/separator messages without proper content
      if ((msg.role === 'separator' || msg.role === 'system') && !msg.content) {
        return false;
      }
      return true;
    });
    
    const lastMessage = filteredMessages[filteredMessages.length - 1];
    const isDuplicateSeparator = lastMessage && lastMessage.role === 'separator' && lastMessage.showNewConversation;
    
    // Check if the last message is a reset message
    const hasResetMessage = lastMessage && lastMessage.role === 'user' && lastMessage.content === 'reset';
    
    if (!isDuplicateSeparator && filteredMessages.length > 0) {
      const separatorMessage: Message = {
        id: COPILOT_CONSTANTS.MESSAGE_ID_PREFIXES.SYSTEM + Date.now(),
        role: 'separator',
        content: COPILOT_CONSTANTS.MESSAGES.NEW_CONVERSATION,
        timestamp: Date.now(),
        showNewConversation: true,
        separator: 'NewConversation',
        from: 'newConversation'
      };
      // If there's a reset message, keep it along with the separator
      setMessages([...filteredMessages, separatorMessage]);
    } else if (filteredMessages.length > 0) {
      setMessages(filteredMessages);
    } else {
      // If no messages left, just clear everything
      setMessages([]);
    }
    
    setFollowUpQuestions([]);
    setPersistentFormValues({});
    setCurrentActiveFormId(null);
    setSubmittedFormIds(new Set());
    setCurrentTicketNumber(null);
    sessionStorage.removeItem('mcapsCopilot_ticketNumber');
    sessionIdRef.current = COPILOT_CONSTANTS.SESSION.ID_PREFIX + Math.random().toString(36).slice(2);
  };

  // Verbatim from SimpleCopilotPanel.tsx L742-771
  const handleFullClear = () => {
    if (controlRef.current?.setInputText) {
      controlRef.current.setInputText("");
    }
    setFollowUpQuestions([]);
    setMessages([]);
    setPersistentFormValues({});
    setCurrentActiveFormId(null);
    setSubmittedFormIds(new Set());
    setCurrentTicketNumber(null);
    sessionStorage.removeItem('mcapsCopilot_ticketNumber');
    sessionIdRef.current = COPILOT_CONSTANTS.SESSION.ID_PREFIX + Math.random().toString(36).slice(2);
    maintainHistory('RESET');
    setLoading(false);
    setIsPromptGalleryOpen(false);
    setShowChangeTopic(true);
    mcapsTelemetry.logNewConversationAction({
      action: 'click',
      component: 'SimpleCopilotPanel'
    });
    const refreshPromptGalleryData = async () => {
      try {
        if (platformContext) {
          await PromptGalleryService.initializeGlobalPromptData(platformContext, { token: "consumption" });
        }
      } catch (error) {
      }
    };
    refreshPromptGalleryData();
  };

  // ========== Effects ==========

  // Verbatim from SimpleCopilotPanel.tsx L224-242
  // Re-fetch prompt gallery data and force re-render when tile appName changes
  React.useEffect(() => {
    const handleAppNameChanged = async () => {
      PromptGalleryService.getInstance().invalidateCache();
      try {
        if (platformContext) {
          await PromptGalleryService.initializeGlobalPromptData(platformContext, { token: "consumption" });
        }
      } catch {
        // Non-fatal
      }
      setAppNameVersion((v: number) => v + 1);
    };

    window.addEventListener('mcapsCopilot_appNameChanged', handleAppNameChanged);
    return () => {
      window.removeEventListener('mcapsCopilot_appNameChanged', handleAppNameChanged);
    };
  }, [platformContext]);

  // Verbatim from SimpleCopilotPanel.tsx L244-257
  // History persistence effect
  React.useEffect(() => {
    if (messages.length > 0 || followUpQuestions.length > 0) {
      maintainHistory('SET', {
        messages,
        followUpQuestions,
        currentActiveFormId,
        submittedFormIds: Array.from(submittedFormIds),
        sessionId: sessionIdRef.current,
        ts: Date.now()
      });
    } else {
      maintainHistory('SET', null);
    }
  }, [messages, followUpQuestions, currentActiveFormId, submittedFormIds, maintainHistory]);

  // Verbatim from SimpleCopilotPanel.tsx L294-357
  // Handle session timeout - create new sessionId and notify user
  // FIX: Use functional setState to avoid stale closure on messages (was PRE-EXISTING race condition)
  React.useEffect(() => {
    const handleSessionExpired = () => {
      // Log telemetry
      mcapsTelemetry.logComponentInteraction({
        action: 'click',
        component: 'SimpleCopilotPanel - Session Expired'
      });
      
      // Create new sessionId
      const newSessionId = COPILOT_CONSTANTS.SESSION.ID_PREFIX + Math.random().toString(36).slice(2);
      sessionIdRef.current = newSessionId;
      
      // Add session expiration message to chat (with inline guard against duplicates)
      const sessionExpiredMessage: Message = {
        id: COPILOT_CONSTANTS.MESSAGE_ID_PREFIXES.SYSTEM + Date.now() + Math.random(),
        role: 'separator',
        content: 'Your session has timed out. Please start a new session by clicking on "New Conversation" or use the refresh button on Copilot.',
        timestamp: Date.now(),
        showNewConversation: false,
        showSignInButton: true,
        separator: 'SessionExpired',
        from: 'sessionTimeout'
      };
      setMessages(prev => {
        // Guard: skip if timeout message already exists (prevents duplicates from rapid expirations)
        const hasTimeoutMessage = prev.some(
          msg => msg.role === 'separator' && msg.showSignInButton === true
        );
        if (hasTimeoutMessage) return prev;
        return [...prev, sessionExpiredMessage];
      });
      
      // Clear follow-up questions and form state
      setFollowUpQuestions([]);
      setCurrentActiveFormId(null);
      setSubmittedFormIds(new Set());
      setCurrentTicketNumber(null);
      sessionStorage.removeItem('mcapsCopilot_ticketNumber');
      
      // Update session history with new sessionId
      setMessages(prev => {
        const updated = [...prev];
        maintainHistory('SET', {
          messages: updated,
          followUpQuestions: [],
          currentActiveFormId: null,
          submittedFormIds: [],
          sessionId: newSessionId,
          ts: Date.now()
        });
        return updated;
      });
    };
    
    // Register callback for session expiration
    onSessionExpired(handleSessionExpired);
    
    // Cleanup on unmount - remove this specific callback
    return () => {
      clearSessionExpiredCallback(handleSessionExpired);
    };
  }, [mcapsTelemetry, maintainHistory]);

  // Verbatim from SimpleCopilotPanel.tsx L359-388
  // Listen for refresh button click from MCAPSCopilotPanelWrapper
  React.useEffect(() => {
    const handleRefreshEvent = async (event: CustomEvent) => {
      setLoading(true);
      setFollowUpQuestions([]); // Clear follow-up questions
      
      try {
        // Send reset to backend (no UI message or separator shown)
        await sendResetToBackend(false);
      } catch (error) {
        console.error('Error during refresh reset:', error);
      } finally {
        setLoading(false);
      }
      
      // Generate new session ID
      sessionIdRef.current = COPILOT_CONSTANTS.SESSION.ID_PREFIX + Math.random().toString(36).slice(2);
      
      // Clear stored data and update history
      sessionStorage.removeItem('mcapsCopilot_ticketNumber');
      setCurrentTicketNumber(null);
      maintainHistory('RESET');
    };
    
    window.addEventListener('mcapsCopilot_triggerReset', handleRefreshEvent as EventListener);
    
    return () => {
      window.removeEventListener('mcapsCopilot_triggerReset', handleRefreshEvent as EventListener);
    };
  }, [effectiveApi, platformAuthClient, maintainHistory]);

  return {
    clearConversation,
    handleResetConversation,
    handleFullClear,
    maintainHistory,
    sendResetToBackend,
  };
}
